import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import Row from '../../../../components/GridSystem/Row';
import styles from './DashboardContent.module.scss';
import Content from '../../../../components/Content/Content';

import ContentPageHeader from '../../../../components/ContentPageHeader/ContentPageHeader';
import DashboardCards from '../DashboardCards/DashboardCards';
import { useUserContext } from '../../../../context/UserContext';
import { useLibraryContext } from '../../../../context/LibraryContext';
import RecordingActions from '../RecordingActions/RecordingActions';
import useFetchListWithInfiniteScroll from '../../../../hooks/useFetchListWithInfiniteScroll';
import VideoService from '../../../../service/VideoService';
import FoldersList from '../FoldersList/FoldersList';
import useFetchFoldersList from '../../../../hooks/useFetchFoldersList';
import { useTranslation } from 'react-i18next';
import CreateFolderModal from '../../../../components/CreateFolderModal/CreateFolderModal';
import RightSidebarMenu from '../../../../components/RightVideoActionMenu/RightSidebarMenu';
import ShareFolderModal from '../../../../components/ShareFolderModal/ShareFolderModal';
import { approvalStatusesForFilter } from '../VideosFilter/constants';
import MobileSearchModal from '../../../../components/MobileSearchModal/MobileSearchModal';
import SearchModalContent from '../../../../components/SearchModalContent/SearchModalContent';
import UploadVideoModal from '../../../../components/UploadVideoModal/UploadVideoModal';
import { AnimationWrapper } from '../../../../components/AnimationWrapper/AnimationWrapper';
import MobileUploadButton from '../../../../components/MobileUploadButton/MobileUploadButton';

const DashboardContent = ({
  userLoading,
  isWebActivated,
  isMdAndBelow,
  isListView,
  handleSetGridView,
  handleSetListView,
  searchModal,
  closeSearchModal,
}) => {
  const { user, isApprovalFlowEnabled, isStoryboardGeneratorEnabled } = useUserContext();

  const { t } = useTranslation();

  const [searchValue, setSearchValue] = useState({ query: '', tags: [] });
  const [folderToShare, setFolderToShare] = useState({});
  const [selectedFilter, setSelectedFilter] = useState({
    TYPE: approvalStatusesForFilter.ALL_VIDEOS.TYPE,
    DESCRIPTION: approvalStatusesForFilter.ALL_VIDEOS.DESCRIPTION,
  });

  const [animate, setAnimate] = useState(false);
  const [prevState, setPrevState] = useState(state);

  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);
  const [isShowUploadVideoModal, setIsShowUploadVideoModal] = useState(false);

  const { state } = useLocation();
  const history = useHistory();

  const onInputChange = useCallback(
    value => {
      if (typeof value === 'string') {
        setSearchValue(prevState => ({ ...prevState, query: value }));
        if (state?.folders || state?.team) {
          searchInputFolders.onChange(value);
          return;
        }
        searchInputVideos.onChange('searchTerm', value);
      } else {
        setSearchValue(prevState => ({ ...prevState, tags: value }));
        searchInputVideos.onChange(
          'tags',
          value.map(item => item.id)
        );
      }
    },
    [state]
  );

  const { userFullName } = useMemo(() => {
    if (user) {
      const fullName = user.userFullName;
      return { userFullName: fullName };
    }
    return {};
  }, [user]);

  const getVideosWithStats = useCallback(async query => {
    console.log('getVideosWithStats query', query);
    query.folder = null;
    query.omitFiledStories = !query.searchTerm;
    query.include_empty = true;
    return await VideoService.getVideos(query);
  }, []);

  const {
    infiniteRef,
    list: videos,
    setList: setVideos,
    totalData,
    loadList: loadVideos,
    isEmptyList: isVideosEmpty,
    isSearching,
    loading,
    searchInput: searchInputVideos,
    query,
    setTotalData,
    loadAllList,
    onApprovalStatusFilter,
  } = useFetchListWithInfiniteScroll({ service: getVideosWithStats });

  const {
    list: folders,
    loadList: loadFolders,
    query: folderQuery,
    loading: folderLoading,
    addNewFolderPlaceholder,
    newFolderCreation,
    newFolderName,
    createFolder,
    cancelFolderCreation,
    searchInput: searchInputFolders,
    sharedFolderWithUsers,
    editFolderUsers,
    deleteFolderUsers,
    updateFolderList,
    changeFolderEditingRight,
    usersOutsideCompany,
    setUsersOutsideCompany,
    isShowShareFolderModal,
    setIsShowShareFolderModal,
  } = useFetchFoldersList({});

  const setVideosAndTotalData = useCallback(
    updatedVideos => {
      setVideos(updatedVideos);
      setTotalData(totalData - 1);
    },
    [setTotalData, setVideos, totalData]
  );

  const handleChangeApprovalFilter = value => {
    if (selectedFilter.TYPE === value.TYPE) {
      return;
    }

    setVideos([]);
    setTotalData(0);
    setSelectedFilter(value);
    onApprovalStatusFilter(value.TYPE);
    toggleUnselectCards();
    history.replace({ state: { ...state, filter: value } });
  };

  useEffect(() => {
    if (state?.filter !== null && state?.filter !== undefined) {
      handleChangeApprovalFilter(state.filter);
    }
  }, []);

  const {
    selectedCard,
    selectedFolder,
    toggleUnselectCards,
    toggleUnselectFolders,
    showRecordingActions,
    toggleShowRecordingActions,
    disableShowRecordingActions,
  } = useLibraryContext();

  useEffect(() => {
    return () => onInputChange('');
  }, [state]);

  const getTitle = () => {
    if (state?.folders) {
      return t('dashboard.folders.title');
    }

    if (state?.team) {
      return t('dashboard.team_folders.title');
    }

    return t('dashboard.videos.title');
  };

  const handleCloseShareModal = () => {
    setIsShowShareFolderModal(false);
    setUsersOutsideCompany([]);
  };

  const shareFolder = folder => {
    setFolderToShare(folder);
    setIsShowShareFolderModal(true);
    toggleUnselectFolders();
  };

  const handleNewProject = () => {
    disableShowRecordingActions();
    history.push('/eg/storyboard/new', { create: !isStoryboardGeneratorEnabled });
  };

  useEffect(() => {
    if (!(state && prevState && ((state?.team && prevState?.team) || (state?.folders && prevState?.folders)))) {
      setAnimate(prevState => !prevState);
    }
    setPrevState(state);
  }, [state]);

  if (isWebActivated === undefined) {
    return null;
  }

  return (
    <Content className={styles.content} triggerManualAnimation={animate}>
      {!userLoading && (
        <>
          <Row className={styles.dashboard}>
            <div className={styles.dashboard}>
              <ContentPageHeader
                userFullName={userFullName}
                isMdAndBelow={isMdAndBelow}
                isListView={isListView}
                handleSetGridView={handleSetGridView}
                handleSetListView={handleSetListView}
                searchValue={searchValue}
                handleInputChange={onInputChange}
                title={getTitle()}
                mode="LIBRARY"
                isVideosTab={!state?.folders && !state?.team}
                handleCreateNewFolder={addNewFolderPlaceholder}
                className={styles.headerOnWebEmpty}
              />
              {!state?.folders && !state?.team && (
                <>
                  <div className={styles.uploadButton}>
                    {isMobileView && <MobileUploadButton setIsShowUploadVideoModal={setIsShowUploadVideoModal} />}
                  </div>

                  <DashboardCards
                    searchInput={searchValue}
                    videos={videos}
                    listMode={isListView}
                    loadVideos={loadAllList}
                    loadFolders={loadFolders}
                    setVideosAndTotalData={setVideosAndTotalData}
                    cardsHeaderId="cardsHeaderID"
                    query={query}
                    loading={loading}
                    isVideosEmpty={isVideosEmpty}
                    infiniteRef={infiniteRef}
                    selectedFilter={selectedFilter}
                    handleChangeApprovalFilter={handleChangeApprovalFilter}
                    isApprovalFlowEnabled={isApprovalFlowEnabled}
                    isWebActivated={isWebActivated}
                    totalData={totalData}
                  />
                </>
              )}
              {/*Folders*/}
              {state?.folders && (
                <FoldersList
                  folderLoading={folderLoading}
                  folders={folders}
                  listMode={isListView}
                  loadFolders={loadFolders}
                  loadVideos={loadAllList}
                  searchInput={searchValue.query}
                  shareFolder={shareFolder}
                  updateFolderList={updateFolderList}
                  folderQuery={folderQuery}
                />
              )}
              {/*Team*/}
              {state?.team && (
                <FoldersList
                  folders={folders}
                  isTeam
                  listMode={isListView}
                  loadFolders={loadFolders}
                  loadVideos={loadAllList}
                  searchInput={searchValue.query}
                  shareFolder={shareFolder}
                  updateFolderList={updateFolderList}
                  folderQuery={folderQuery}
                  folderLoading={folderLoading}
                />
              )}
              <AnimationWrapper show={showRecordingActions} from={{ opacity: 0 }} to={{ opacity: 1 }} options={{ duration: 100 }}>
                <RecordingActions
                  disableShowRecordingActions={disableShowRecordingActions}
                  onRedirect={handleNewProject}
                  handleShowUploadVideoModal={() => setIsShowUploadVideoModal(true)}
                />
              </AnimationWrapper>
              <UploadVideoModal
                isShow={isShowUploadVideoModal}
                onClose={() => setIsShowUploadVideoModal(false)}
                loadVideos={loadAllList}
              />
              <CreateFolderModal
                isShow={newFolderCreation}
                defaultName={newFolderName}
                onClose={cancelFolderCreation}
                createFolder={createFolder}
              />
              {isShowShareFolderModal && (
                <ShareFolderModal
                  isRedirect
                  sharedFolderWithUsers={sharedFolderWithUsers}
                  editFolderUsers={editFolderUsers}
                  deleteFolderUsers={deleteFolderUsers}
                  changeFolderEditingRight={changeFolderEditingRight}
                  onClose={handleCloseShareModal}
                  isShow={isShowShareFolderModal}
                  folder={folderToShare?.id ? folderToShare : folders[0]}
                  usersOutsideCompany={usersOutsideCompany}
                />
              )}
            </div>
          </Row>
          <MobileSearchModal open={searchModal} onClose={closeSearchModal}>
            <SearchModalContent onClose={closeSearchModal} tab={state} selectedFilter={selectedFilter} />
          </MobileSearchModal>
          {!!(Object.keys(selectedCard).length || Object.keys(selectedFolder).length) && (
            <RightSidebarMenu userId={user.username} shareFolder={shareFolder} />
          )}
        </>
      )}
    </Content>
  );
};

export default DashboardContent;
